import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiMapPin, FiNavigation } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import useSocket from "../../../hooks/useSocket";
import { repoCaseService } from "../../../services/repoCase.service";
import { fieldTrackingService } from "../../../services/fieldTracking.service";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import {
  canSendFieldGps,
  canViewFleetMap,
} from "../../../utils/fieldTrackingPermissions";
import { actsAsAgencyAdmin } from "../../../utils/permissions";
import { isMongoCaseId } from "../vehicles/findVehiclesHelpers";
import { displayUserName } from "../../../utils/displayUserName";
import { formatRepoRole } from "../../../constants/repoRoles";
import { readMyLastLocation } from "../../../constants/locationPermission";
import "../../../styles/users.css";
import "../../../styles/fieldMap.css";
import CaseLocationsMapView from "../../../components/tracking/CaseLocationsMapView";
import FieldMapView from "../../../components/tracking/FieldMapView";

function mergeTracer(list, incoming) {
  if (!incoming?.tracerId && incoming?.latitude == null) return list;
  const tracerId = String(incoming.tracerId || incoming.id || "me");
  const row = {
    tracerId,
    name: displayUserName({
      name: incoming.tracerName || incoming.name,
      role: incoming.role,
    }),
    phone: incoming.phone || "",
    post: incoming.post || "",
    role: incoming.role || "REPO_STAFF",
    latitude: incoming.latitude,
    longitude: incoming.longitude,
    accuracy: incoming.accuracy,
    updatedAt: incoming.updatedAt || new Date().toISOString(),
    caseId: incoming.caseId || null,
    vehicleNumber: incoming.vehicleNumber || "",
  };
  const idx = list.findIndex((t) => t.tracerId === tracerId);
  if (idx === -1) return [row, ...list];
  const next = [...list];
  next[idx] = { ...next[idx], ...row };
  return next;
}

function LiveAgentsMap({ auth, viewFleet, sendGps }) {
  const [tracers, setTracers] = useState([]);
  const [error, setError] = useState("");
  const selfId = String(auth?.user?.id || auth?.user?._id || "me");

  const loadTracers = useCallback(async () => {
    if (!auth?.token) return;
    if (!viewFleet && !sendGps) return;
    try {
      const res = await fieldTrackingService.getLiveTracers(auth.token);
      const items = res?.data?.items || [];
      setTracers((prev) => {
        let next = items;
        const mine = readMyLastLocation();
        if (mine?.latitude != null && mine?.longitude != null) {
          next = mergeTracer(next, {
            ...mine,
            tracerId: mine.tracerId || selfId,
            name: mine.name || auth?.user?.name || "You",
            role: auth?.user?.role,
          });
        }
        return next;
      });
      setError("");
    } catch (err) {
      const mine = readMyLastLocation();
      if (mine?.latitude != null) {
        setTracers((prev) =>
          mergeTracer(prev, {
            ...mine,
            tracerId: mine.tracerId || selfId,
            name: mine.name || auth?.user?.name || "You",
            role: auth?.user?.role,
          })
        );
        setError("");
        return;
      }
      setError(err?.response?.data?.message || "Could not load live location.");
    }
  }, [auth?.token, auth?.user, viewFleet, sendGps, selfId]);

  useEffect(() => {
    loadTracers();
    const id = window.setInterval(loadTracers, 15000);
    return () => window.clearInterval(id);
  }, [loadTracers]);

  useEffect(() => {
    const mine = readMyLastLocation();
    if (mine?.latitude != null && mine?.longitude != null) {
      setTracers((prev) =>
        mergeTracer(prev, {
          ...mine,
          tracerId: mine.tracerId || selfId,
          name: mine.name || auth?.user?.name || "You",
        })
      );
    }
  }, [auth?.user?.name, selfId]);

  const onSocketEvent = useCallback((event, payload) => {
    if (event === "tracer:location" && payload) {
      setTracers((prev) => mergeTracer(prev, payload));
    }
  }, []);
  useSocket(onSocketEvent);

  useEffect(() => {
    const onMine = (event) => {
      const detail = event?.detail;
      if (detail?.latitude == null) return;
      setTracers((prev) =>
        mergeTracer(prev, {
          ...detail,
          tracerId: detail.tracerId || selfId,
          name: detail.name || auth?.user?.name || "You",
        })
      );
    };
    window.addEventListener("repo:my-location", onMine);
    return () => window.removeEventListener("repo:my-location", onMine);
  }, [auth?.user?.name, selfId]);

  const visible = tracers.filter((t) => t.latitude != null && t.longitude != null);

  return (
    <div className="create-company-card">
      <h3>Live agent location</h3>
      {error ? <p className="error-text">{error}</p> : null}
      {visible.length === 0 ? (
        <p className="muted">
          {sendGps
            ? "Waiting for your GPS update. Allow location if prompted, then this map will show your position."
            : "No live agent GPS yet. Field staff must enable location after login."}
        </p>
      ) : (
        <>
            <FieldMapView tracers={visible} followAgent={visible.length === 1} />
          <ul className="lrms-note-box" style={{ listStyle: "none", padding: 0, marginTop: 12 }}>
            {visible.map((t) => (
              <li key={t.tracerId} style={{ marginBottom: 8 }}>
                <strong>{t.name}</strong> · {formatRepoRole(t.role)}
                <br />
                {Number(t.latitude).toFixed(5)}, {Number(t.longitude).toFixed(5)}
                {t.updatedAt ? ` · ${new Date(t.updatedAt).toLocaleString()}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function LiveTracking() {
  const { auth } = useAuth();
  const role = auth?.user?.role;
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get("caseId") || "";

  const [caseData, setCaseData] = useState(null);
  const [traceStatuses, setTraceStatuses] = useState([]);
  const [traceStatus, setTraceStatus] = useState("PENDING");
  const [timeline, setTimeline] = useState([]);
  const [lastLocation, setLastLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const sendGps = canSendFieldGps(role);
  const viewFleet = canViewFleetMap(role) || actsAsAgencyAdmin(auth);
  const persistedCase = isMongoCaseId(caseId);

  const loadCase = useCallback(async (id) => {
    if (!auth?.token || !id) return;
    try {
      const res = await repoCaseService.getCaseById(id, auth.token);
      const doc = res?.data || res;
      setCaseData(doc);
      setTraceStatus(doc?.traceStatus || "PENDING");
      setLastLocation(doc?.lastKnownLocation || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load case.");
    }
  }, [auth?.token]);

  const loadTimeline = useCallback(async (id) => {
    if (!auth?.token || !id) return;
    try {
      const res = await fieldTrackingService.getTimeline(id, auth.token);
      const data = res?.data || res;
      setTimeline(data?.timeline || []);
      if (data?.lastKnownLocation) setLastLocation(data.lastKnownLocation);
      if (data?.traceStatus) setTraceStatus(data.traceStatus);
    } catch {
      /* optional */
    }
  }, [auth?.token]);

  const loadLocations = useCallback(async (id) => {
    if (!auth?.token || !id) return;
    setLocationsLoading(true);
    try {
      const res = await fieldTrackingService.getLocations(id, auth.token);
      const items = res?.data?.items || [];
      setLocations(items);
    } catch {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    fieldTrackingService
      .getTraceStatuses(auth?.token)
      .then((res) => setTraceStatuses(res?.data || []))
      .catch(() => {});
  }, [auth?.token]);

  useEffect(() => {
    if (!auth?.token || !persistedCase) return undefined;
    setError("");
    loadCase(caseId);
    loadTimeline(caseId);
    loadLocations(caseId);
    return undefined;
  }, [auth?.token, caseId, persistedCase, loadCase, loadTimeline, loadLocations]);

  const handleTraceStatusSave = async () => {
    if (!persistedCase) return;
    setError("");
    try {
      await fieldTrackingService.patchTraceStatus(
        caseId,
        { traceStatus },
        auth.token
      );
      setStatus("Trace status updated.");
      await loadTimeline(caseId);
      await loadCase(caseId);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not update status.");
    }
  };

  const handleMarkTraced = async () => {
    if (!persistedCase) return;
    setError("");
    try {
      await fieldTrackingService.patchTraceStatus(
        caseId,
        { traceStatus: "TRACED" },
        auth.token
      );
      setTraceStatus("TRACED");
      setStatus("Marked as Traced.");
      await loadTimeline(caseId);
      await loadCase(caseId);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not mark as Traced.");
    }
  };

  const mapsUrl =
    lastLocation?.latitude != null && lastLocation?.longitude != null
      ? `https://www.google.com/maps?q=${lastLocation.latitude},${lastLocation.longitude}`
      : null;

  if (!persistedCase) {
    return (
      <div className="page">
        <div className="create-company-header">
          <div>
            <h2>Live tracking</h2>
            <p className="muted">
              Your live GPS is shared automatically after you allow location at login.
              {viewFleet
                ? " Open Field map to see tracers."
                : " Admins and team leaders can see you on the Field map."}
            </p>
          </div>
          <div className="create-company-actions">
            {viewFleet ? (
              <Link to="/field-map" className="secondary-page-btn">
                Field map
              </Link>
            ) : null}
            <Link to="/find-vehicles" className="primary-page-btn">
              Find vehicle
            </Link>
          </div>
        </div>
        <LiveAgentsMap auth={auth} viewFleet={viewFleet} sendGps={sendGps} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="create-company-header">
        <div>
          <h2>Live tracking</h2>
          <p className="muted">
            {caseData
              ? `${formatVehicleNumberDisplay(caseData.vehicleNumber)} · ${caseData.customerName || ""}`
              : "Loading case…"}
          </p>
        </div>
        <div className="create-company-actions">
          {viewFleet ? (
            <Link to="/field-map" className="secondary-page-btn">
              Field map
            </Link>
          ) : null}
          <Link to="/find-vehicles" className="secondary-page-btn">
            Find vehicle
          </Link>
        </div>
      </div>

      <div className="create-company-card">
        <h3>GPS location</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Field status: <strong>{traceStatus || "PENDING"}</strong>
        </p>
        {lastLocation?.latitude != null ? (
          <p>
            <FiMapPin aria-hidden /> {lastLocation.latitude.toFixed(5)},{" "}
            {lastLocation.longitude.toFixed(5)}
            {lastLocation.updatedAt &&
              ` · ${new Date(lastLocation.updatedAt).toLocaleString()}`}
            {lastLocation.tracerName ? ` · ${lastLocation.tracerName}` : ""}
          </p>
        ) : (
          <p className="muted">No case location snapshots yet. Live user GPS is on the Field map.</p>
        )}
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="company-btn company-btn--view"
          >
            <FiNavigation aria-hidden /> Open in Google Maps
          </a>
        )}
        {sendGps ? (
          <p className="muted" style={{ marginTop: 12 }}>
            Your live location is sent automatically while you are logged in. You do not need to
            capture GPS on this page.
          </p>
        ) : (
          <p className="muted" style={{ marginTop: 12 }}>
            View only. Field staff share live GPS after login. Use{" "}
            <Link to="/field-map">Field map</Link> to watch tracers.
          </p>
        )}
      </div>

      <div className="create-company-card">
        <h3>Field trace status</h3>
        <div className="form-grid two-column">
          <div className="form-group">
            <label>Status</label>
            <select
              value={traceStatus}
              onChange={(e) => setTraceStatus(e.target.value)}
              disabled={!sendGps && !actsAsAgencyAdmin(auth)}
            >
              {traceStatuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          {(sendGps || actsAsAgencyAdmin(auth)) && (
            <div className="form-group phone-otp-actions">
              <label>&nbsp;</label>
              <div className="create-company-actions">
                <button type="button" className="primary-page-btn" onClick={handleTraceStatusSave}>
                  Save status
                </button>
                {sendGps && traceStatus !== "TRACED" ? (
                  <button type="button" className="secondary-page-btn" onClick={handleMarkTraced}>
                    Mark as Traced
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {status && <p className="cfm-status">{status}</p>}
      {error && <p className="error-text">{error}</p>}

      <div className="create-company-card">
        <h3>Case timeline</h3>
        {timeline.length === 0 ? (
          <p className="muted">No timeline entries yet.</p>
        ) : (
          <ul className="lrms-note-box" style={{ listStyle: "none", padding: 0 }}>
            {[...timeline].reverse().map((entry, i) => (
              <li key={i} style={{ marginBottom: 8 }}>
                <strong>{entry.at ? new Date(entry.at).toLocaleString() : ""}</strong>
                <br />
                {entry.summary || entry.type}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="create-company-card">
        <h3>Tracer locations</h3>
        {locationsLoading ? (
          <p className="muted">Loading locations…</p>
        ) : locations.length === 0 ? (
          <p className="muted">No location snapshots yet.</p>
        ) : (
          <>
            <CaseLocationsMapView locations={locations} />
            <p className="muted" style={{ marginTop: 10 }}>
              Showing <strong>{locations.length}</strong> tracer updates for this case.
            </p>
            <ul className="lrms-note-box" style={{ listStyle: "none", padding: 0 }}>
              {[...locations].slice(0, 8).map((l, idx) => (
                <li key={`${l.createdAt || l.updatedAt || idx}`} style={{ marginBottom: 8 }}>
                  <strong>{l.tracerName || "Tracer"}</strong>
                  <br />
                  Lat/Lng:{" "}
                  {l.latitude != null && l.longitude != null
                    ? `${Number(l.latitude).toFixed(5)}, ${Number(l.longitude).toFixed(5)}`
                    : "—"}
                  <br />
                  {l.createdAt ? new Date(l.createdAt).toLocaleString() : "—"}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
